import type { ListBuilderConfiguration } from '../types';

/**
 * Team list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const TEAM_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-teams-all',
			cacheTTL: 600000,
			name: 'All Teams',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'team-name',
					fields: [
						{
							id: 'name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'team_name',
									objectTable: 'team',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{
							id: 'h1',
							text: 'Name',
						},
					],
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'team_name',
							objectTable: 'team',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'member-count',
					fields: [
						{
							id: 'member-count-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'member_count',
									objectTable: 'team',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{
							id: 'h1',
							text: 'Members',
						},
					],
					minWidth: 100,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'member_count',
							objectTable: 'team',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'top',
				},
				{
					// PD-16315 — where the team sits in the tree, as the chain
					// of teams above it. Blank for a top-level team, which is
					// the honest rendering: it is not nested under anything.
					id: 'team-parent-path',
					fields: [
						{
							id: 'parent-path-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'parent_path',
									objectTable: 'team',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{
							id: 'h1',
							text: 'Nested under',
						},
					],
					minWidth: 160,
					sortable: false,
					alignment: 'left',
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'team-type',
					fields: [
						{
							id: 'team-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'team_type_name',
									objectTable: 'team',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{
							id: 'h1',
							text: 'Type',
						},
					],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'team_type_name',
							objectTable: 'team',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'created-date',
					fields: [
						{
							id: 'created-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'created_date',
									objectTable: 'team',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'date',
							displayConfig: {
								dateFormat: 'MMM d, yyyy',
							},
						},
					],
					headers: [
						{
							id: 'h1',
							text: 'Created',
						},
					],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'created_date',
							objectTable: 'team',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					headers: [
						{
							id: 'actions-header',
							text: '',
						},
					],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'pen',
									callbackName: 'editTeam',
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'teams:update',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deleteTeam',
									variant: 'danger',
									visibility: 'visible',
									requiredPermission: 'teams:delete',
								},
							],
						},
					],
					sortable: false,
					width: 80,
					align: 'center',
				},
			],
			// PD-16315 — the sort control previously showed "Created (Newest
			// First)" while the rows came back by name, because no default was
			// declared and the endpoint ignored the sort entirely. Both halves
			// now say the same thing.
			defaultSortId: 'team-name-asc',
		},
	],
	// Root-level tableSorts so `defaultSortId` resolves — a list-level
	// `sortFields` is not read by the resolver (PD-14536).
	tableSorts: [
		{
			id: 'team-name-asc',
			name: 'Name A → Z',
			fields: [
				{
					objectTable: 'team',
					fieldName: 'team_name',
					direction: 'ASC',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: ['teamKeywords', 'teamType'],
		defaultFilters: {},
	},
	objects: [
		{
			// Teams left the settings tree for the top-level navigation (PD-16035).
			baseUrl: '/teams/:id',
			objectLabel: 'Team',
			objectTable: 'team',
			selectedFields: [
				{ fieldName: 'team_id', fieldLabel: 'ID' },
				{ fieldName: 'team_name', fieldLabel: 'Name' },
				{ fieldName: 'member_count', fieldLabel: 'Member Count' },
				{ fieldName: 'parent_path', fieldLabel: 'Nested Under' },
				{ fieldName: 'team_type_name', fieldLabel: 'Type' },
				{ fieldName: 'created_date', fieldLabel: 'Created Date' },
			],
		},
	],
};
